
import { useState, useRef, FormEvent, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useSearchParams } from "react-router-dom";

interface UseSettingsDialogProps {
  businessName: string;
  country?: string;
  onBusinessNameChange: (value: string) => void;
  onGenerateBusinessName: () => Promise<void>;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (phonePrefix: string, phoneNumber: string | null, businessName: string) => Promise<void>;
}

export const useSettingsDialog = ({
  businessName,
  country = "Kenya",
  onBusinessNameChange,
  onGenerateBusinessName,
  onImageUpload,
  onSubmit
}: UseSettingsDialogProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedPrefix, setSelectedPrefix] = useState<string>(country === "Ethiopia" ? "+251" : "+254");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [preferredMessaging, setPreferredMessaging] = useState<string>(country === "Ethiopia" ? "telegram" : "whatsapp");
  const [currency, setCurrency] = useState<string>(country === "Ethiopia" ? "Birr" : "KES");
  const [businessNameError, setBusinessNameError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();

  // Get UTM parameters from URL
  const getUtmParams = () => {
    return {
      utm_source: searchParams.get("utm_source") || undefined,
      utm_medium: searchParams.get("utm_medium") || undefined,
      utm_campaign: searchParams.get("utm_campaign") || undefined,
      utm_content: searchParams.get("utm_content") || undefined,
      utm_term: searchParams.get("utm_term") || undefined
    };
  };

  useEffect(() => {
    setPreferredMessaging(country === "Ethiopia" ? "telegram" : "whatsapp");
    setSelectedPrefix(country === "Ethiopia" ? "+251" : "+254");
    setCurrency(country === "Ethiopia" ? "Birr" : "KES");
  }, [country]);

  const createSlugFromName = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const sendToHubSpot = async (userData: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return true;

      console.log("Sending user data to HubSpot:", userData);
      
      try {
        const { data, error } = await supabase.functions.invoke('hubspot-contact', {
          body: userData
        });

        if (error) {
          console.error("Error sending data to HubSpot:", error);
        } else {
          console.log("HubSpot response:", data);
        }
      } catch (err) {
        console.error("Failed to send data to HubSpot:", err);
      }
      
      return true;
    } catch (err) {
      console.error("Failed to send data to HubSpot:", err);
      return true; // Always return true to prevent blocking the flow
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    if (!businessName || businessName.trim() === "") {
      setBusinessNameError("Business name is required");
      setIsSubmitting(false);
      return;
    }
    
    setBusinessNameError("");
    
    // Start all processes in parallel
    const promises = [];
    
    // Save service page link
    promises.push(saveServicePageLink().catch(err => {
      console.error('Error saving service page link:', err);
      // Don't block the flow if this fails
    }));
    
    // Save preferred settings including UTM parameters
    promises.push(savePreferredSettings().catch(err => {
      console.error('Error saving preferred settings:', err);
      // Don't block the flow if this fails
    }));
    
    // Save phone number
    if (phoneNumber && phoneNumber.trim() !== '') {
      promises.push((async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const formattedNumber = `${selectedPrefix}${phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber}`;
            
            console.log(`Saving ${preferredMessaging} number:`, formattedNumber);
            
            await supabase
              .from('profiles')
              .update({ whatsapp_number: formattedNumber })
              .eq('id', user.id);
          }
        } catch (err) {
          console.error('Error saving whatsapp number:', err);
          // Don't block the flow if this fails
        }
      })());
    }

    // Send data to HubSpot as a background task
    promises.push((async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, email, profession, country')
            .eq('id', user.id)
            .single();

          if (profile) {
            const fullPhoneNumber = phoneNumber ? 
              `${selectedPrefix}${phoneNumber.startsWith('0') ? phoneNumber.substring(1) : phoneNumber}` : null;
              
            // Don't await this - let it run in the background
            sendToHubSpot({
              firstName: profile.first_name,
              lastName: profile.last_name,
              email: profile.email || user.email,
              phone: fullPhoneNumber,
              profession: profile.profession,
              country: profile.country
            }).catch(err => {
              console.error('Error in sendToHubSpot:', err);
            });
          }
        }
      } catch (err) {
        console.error('Error sending data to HubSpot:', err);
        // Don't block the flow if this fails
      }
    })());
    
    // Wait for all operations in parallel
    try {
      await Promise.allSettled(promises);
    } catch (error) {
      console.error("Error in async operations:", error);
      // Don't block the flow if this fails
    }
    
    // Submit the form
    try {
      await onSubmit(selectedPrefix, phoneNumber || null, businessName);
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
      });
      setIsSubmitting(false);
    }
  };

  const handleBusinessNameChange = (value: string) => {
    onBusinessNameChange(value);
    if (value.trim() !== "") {
      setBusinessNameError("");
    }
  };

  const saveServicePageLink = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const slug = businessName ? createSlugFromName(businessName) : '';
      const serviceLink = slug 
        ? `services/${slug}-${user.id.substring(0, 8)}`
        : `services/${user.id.substring(0, 8)}`;
      
      const { error } = await supabase
        .from('profiles')
        .update({ service_page_link: serviceLink })
        .eq('id', user.id);
        
      if (error) console.error('Error saving service page link:', error);
    } catch (err) {
      console.error('Error in saveServicePageLink:', err);
    }
  };

  const savePreferredSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // Get UTM parameters
      const utmParams = getUtmParams();
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          preferred_messaging: preferredMessaging,
          currency: currency,
          // Include UTM parameters if they exist in the URL
          ...(utmParams.utm_source ? { utm_source: utmParams.utm_source } : {}),
          ...(utmParams.utm_medium ? { utm_medium: utmParams.utm_medium } : {}),
          ...(utmParams.utm_campaign ? { utm_campaign: utmParams.utm_campaign } : {}),
          ...(utmParams.utm_content ? { utm_content: utmParams.utm_content } : {}),
          ...(utmParams.utm_term ? { utm_term: utmParams.utm_term } : {})
        })
        .eq('id', user.id);
        
      if (error) console.error('Error saving preferred settings:', error);
    } catch (err) {
      console.error('Error in savePreferredSettings:', err);
    }
  };

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  return {
    fileInputRef,
    selectedPrefix,
    phoneNumber,
    preferredMessaging,
    currency,
    businessNameError,
    handleSubmit,
    handleBusinessNameChange,
    handleFileInputClick,
    setSelectedPrefix,
    setPhoneNumber,
    setPreferredMessaging,
    setCurrency,
    isSubmitting,
  };
};
